import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'

import { handleException } from 'clients/exception-handling'
import type { AgentInsightService } from 'features/trend-detection/features/insight-agent/service'
import type { AgentInsightAgent } from 'features/trend-detection/features/insight-agent/types'

export interface UseAgentConfigResult {
  agent: AgentInsightAgent | undefined
  isLoading: boolean
  /** Draft form state; seeded from the agent once it loads. */
  name: string
  prompt: string
  setName: (value: string) => void
  setPrompt: (value: string) => void
  /** True once the draft differs from what is stored. */
  isDirty: boolean
  isSaving: boolean
  saveError: boolean
  save: () => Promise<void>
  reset: () => void
}

/**
 * View/edit one agent's configuration — its name and prompt.
 *
 * Deliberately does NOT load runs or insights: the config page is only for
 * seeing and editing what the agent IS. Run history and output live in the
 * insights feed.
 */
export function useAgentConfig(
  service: AgentInsightService,
  projectId: string | null,
  agentUuid: string | undefined,
): UseAgentConfigResult {
  const queryClient = useQueryClient()
  const enabled = Boolean(projectId && agentUuid)

  const { data: agent, isLoading } = useQuery<AgentInsightAgent>({
    queryKey: ['insight-agent', projectId, agentUuid],
    queryFn: () => service.getAgent(projectId!, agentUuid!),
    enabled,
    refetchOnWindowFocus: false,
  })

  const [name, setName] = useState('')
  const [prompt, setPrompt] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState(false)

  // Seed the draft from the loaded agent — ONCE per agent. A ref guard rather
  // than a narrowed dep array: deps stay honest (`agent`), and a refetch cannot
  // clobber edits the user is part-way through.
  const seededFor = useRef<string | null>(null)
  useEffect(() => {
    if (!agent || seededFor.current === agent.uuid) return
    seededFor.current = agent.uuid
    setName(agent.name)
    setPrompt(agent.prompt)
  }, [agent])

  const isDirty =
    agent != null && (name !== agent.name || prompt !== agent.prompt)

  // `save` and `reset` are returned from this hook, so they land in consumers'
  // dependency arrays — memoized to keep them referentially stable across
  // renders rather than re-allocating and re-triggering every effect downstream.
  const save = useCallback(async (): Promise<void> => {
    if (!projectId || !agentUuid || isSaving) return
    setIsSaving(true)
    setSaveError(false)
    try {
      const updated = await service.updateAgent(
        projectId,
        agentUuid,
        name.trim(),
        prompt.trim(),
      )
      queryClient.setQueryData(['insight-agent', projectId, agentUuid], updated)
      // The agents table shows name; keep it from going stale behind this page.
      await queryClient.invalidateQueries(['insight-agents', projectId])
    } catch (cause) {
      handleException(cause, {
        operation: 'agent.insight.config.save',
        disposition: 'failed',
      })

      setSaveError(true)
    } finally {
      setIsSaving(false)
    }
  }, [projectId, agentUuid, isSaving, service, name, prompt, queryClient])

  const reset = useCallback((): void => {
    if (!agent) return
    setName(agent.name)
    setPrompt(agent.prompt)
    setSaveError(false)
  }, [agent])

  return {
    agent,
    isLoading,
    name,
    prompt,
    setName,
    setPrompt,
    isDirty,
    isSaving,
    saveError,
    save,
    reset,
  }
}
